import { supabase } from './supabase'
import { validateFile, getAllowedFileTypesDescription } from './fileValidation'
import { isDemoModeActive } from '../contexts/DemoContext'
import { demoFiles } from '../mocks/demoData'

export interface TodoFile {
  id: string
  todo_id: string
  user_id: string
  file_url: string
  file_name: string
  file_size: number | null
  file_type: string | null
  file_category: 'image' | 'document' | 'video' | 'audio' | 'archive' | 'other'
  display_order: number
  created_at: string
}

const MAX_FILES_PER_TODO = 10
const MAX_FILE_SIZE = 50 * 1024 * 1024 // 50MB

// File category detection
function detectFileCategory(mimeType: string): TodoFile['file_category'] {
  if (mimeType.startsWith('image/')) return 'image'
  if (mimeType.startsWith('video/')) return 'video'
  if (mimeType.startsWith('audio/')) return 'audio'
  if (mimeType.includes('pdf') ||
      mimeType.includes('document') ||
      mimeType.includes('text') ||
      mimeType.includes('word') ||
      mimeType.includes('excel') ||
      mimeType.includes('powerpoint') ||
      mimeType.includes('spreadsheet') ||
      mimeType.includes('presentation')) return 'document'
  if (mimeType.includes('zip') ||
      mimeType.includes('rar') ||
      mimeType.includes('7z') ||
      mimeType.includes('tar') ||
      mimeType.includes('gz')) return 'archive'
  return 'other'
}

/**
 * Upload a file for a todo
 */
export async function uploadTodoFile(
  todoId: string,
  file: File
): Promise<{ success: boolean; file?: TodoFile; error?: string }> {
  // デモモード中はアップロード不可
  if (isDemoModeActive()) {
    return { success: false, error: 'デモモードではファイルのアップロードはできません' }
  }

  try {
    // セキュリティチェック: ファイルタイプ検証
    const validation = validateFile(file)
    if (!validation.isValid) {
      console.warn('File validation failed:', validation.error, 'File:', file.name, 'Type:', file.type)
      return {
        success: false,
        error: validation.error || `不正なファイル形式です。${getAllowedFileTypesDescription('ja')}のみアップロード可能です`
      }
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      return { success: false, error: 'ファイルサイズは50MB以下にしてください' }
    }

    // Check current file count
    const { data: existingFiles, error: countError } = await supabase
      .from('todo_files')
      .select('id')
      .eq('todo_id', todoId)

    if (countError) {
      console.error('Error checking file count:', countError)
      return { success: false, error: 'ファイル数の確認に失敗しました' }
    }

    if (existingFiles && existingFiles.length >= MAX_FILES_PER_TODO) {
      return { success: false, error: `1つのタスクに添付できるファイルは${MAX_FILES_PER_TODO}個までです` }
    }

    // Get user ID
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      return { success: false, error: 'ログインが必要です' }
    }

    // Get todo and project info to determine the owner
    const { data: todo, error: todoError } = await supabase
      .from('todos')
      .select('project_id, projects(user_id, team_id)')
      .eq('id', todoId)
      .single()

    if (todoError || !todo) {
      console.error('Error fetching todo:', todoError)
      return { success: false, error: 'タスク情報の取得に失敗しました' }
    }

    // For team projects, use project owner's ID; otherwise use current user's ID
    const project = todo.projects as any
    const ownerId = project?.team_id ? project.user_id : user.id

    // Generate unique file name
    const fileExt = file.name.split('.').pop()
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`
    const filePath = `${ownerId}/${todoId}/${fileName}`

    // Upload to storage (will use 'task-files' bucket)
    const { error: uploadError } = await supabase.storage
      .from('task-files')
      .upload(filePath, file)

    if (uploadError) {
      console.error('Error uploading file:', uploadError)
      return { success: false, error: 'ファイルのアップロードに失敗しました' }
    }

    // Detect category
    const category = detectFileCategory(file.type)

    // Get next display order
    const nextOrder = existingFiles ? existingFiles.length : 0

    // Save to database (use owner's ID for team projects)
    const insertData = {
      todo_id: todoId,
      user_id: ownerId,
      file_url: filePath,
      file_name: file.name,
      file_size: file.size,
      file_type: file.type,
      file_category: category,
      display_order: nextOrder
    }

    const { data: uploadedFile, error: dbError } = await supabase
      .from('todo_files')
      .insert(insertData)
      .select()
      .single()

    if (dbError) {
      console.error('Error saving file to database:', dbError)
      // Rollback: delete uploaded file
      await supabase.storage.from('task-files').remove([filePath])

      // Check if it's a quota error
      if (dbError.message.includes('Storage quota exceeded')) {
        return { success: false, error: 'ストレージ容量が不足しています。プランをアップグレードしてください。' }
      }

      return { success: false, error: 'ファイル情報の保存に失敗しました' }
    }

    return { success: true, file: uploadedFile }
  } catch (error) {
    console.error('Unexpected error uploading file:', error)
    return { success: false, error: '予期しないエラーが発生しました' }
  }
}

/**
 * Delete a todo file
 */
export async function deleteTodoFile(
  fileId: string
): Promise<{ success: boolean; error?: string }> {
  try {
    // Get file info
    const { data: file, error: fetchError } = await supabase
      .from('todo_files')
      .select('*')
      .eq('id', fileId)
      .single()

    if (fetchError || !file) {
      console.error('Error fetching file:', fetchError)
      return { success: false, error: 'ファイル情報の取得に失敗しました' }
    }

    // Delete from storage
    const { error: storageError } = await supabase.storage
      .from('task-files')
      .remove([file.file_url])

    if (storageError) {
      console.error('Error deleting from storage:', storageError)
      // Continue anyway to clean up database
    }

    // Delete from database
    const { error: dbError } = await supabase
      .from('todo_files')
      .delete()
      .eq('id', fileId)

    if (dbError) {
      console.error('Error deleting from database:', dbError)
      return { success: false, error: 'ファイルの削除に失敗しました' }
    }

    return { success: true }
  } catch (error) {
    console.error('Unexpected error deleting file:', error)
    return { success: false, error: '予期しないエラーが発生しました' }
  }
}

/**
 * Get all files for a todo
 */
export async function getTodoFiles(
  todoId: string
): Promise<{ success: boolean; files?: TodoFile[]; error?: string }> {
  // デモモード中はデモ用ファイルデータを返す
  if (isDemoModeActive()) {
    const files = demoFiles[todoId] || []
    return { success: true, files }
  }

  try {
    const { data: files, error } = await supabase
      .from('todo_files')
      .select('*')
      .eq('todo_id', todoId)
      .order('display_order', { ascending: true })

    if (error) {
      console.error('Error fetching todo files:', error)
      return { success: false, error: 'ファイルの取得に失敗しました' }
    }

    return { success: true, files: files || [] }
  } catch (error) {
    console.error('Unexpected error fetching files:', error)
    return { success: false, error: '予期しないエラーが発生しました' }
  }
}

/**
 * Get signed URL for a file (valid for 1 hour)
 */
export async function getFileUrl(filePath: string, bucketName: string = 'task-files'): Promise<string> {
  // デモモード中はpublicフォルダのパスをそのまま返す
  if (isDemoModeActive() && filePath.startsWith('/')) {
    return filePath
  }

  const { data, error } = await supabase.storage
    .from(bucketName)
    .createSignedUrl(filePath, 3600) // 1 hour expiry

  if (error) {
    console.error('Error creating signed URL:', error)
    return ''
  }

  return data.signedUrl
}

/**
 * Download a file
 */
export async function downloadFile(filePath: string, fileName: string, bucketName: string = 'task-files'): Promise<void> {
  // デモモード中はpublicフォルダから直接ダウンロード
  if (isDemoModeActive() && filePath.startsWith('/')) {
    try {
      const response = await fetch(filePath)
      if (!response.ok) {
        throw new Error('Failed to fetch file')
      }
      const blob = await response.blob()
      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = fileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(blobUrl)
      return
    } catch (error) {
      console.error('Demo download error:', error)
      throw new Error('ダウンロードに失敗しました')
    }
  }

  const url = await getFileUrl(filePath, bucketName)
  if (!url) {
    throw new Error('Failed to generate download URL')
  }

  try {
    // Fetch the file as blob to force download instead of opening in new tab
    const response = await fetch(url)
    if (!response.ok) {
      throw new Error('Failed to fetch file')
    }

    const blob = await response.blob()

    // Create blob URL and trigger download
    const blobUrl = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = blobUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    // Clean up blob URL
    window.URL.revokeObjectURL(blobUrl)
  } catch (error) {
    console.error('Download error:', error)
    throw new Error('ダウンロードに失敗しました')
  }
}
